import { isBrowser } from 'browser-or-node';
import _camelCase from 'lodash/camelCase.js';
import deburr from 'lodash/deburr.js';
import isEmpty from 'lodash/isEmpty.js';
import isNil from 'lodash/isNil.js';
import isString from 'lodash/isString.js';
import upperFirst from 'lodash/upperFirst.js';
import words from 'lodash/words.js';
import { ensureArray } from "./arrays.js";
export const camelCase = _camelCase;
export function formatShapeName(str, options) {
    const strings = ensureArray(str);
    const shapeNameIndex = options?.shapeNameIndex ?? 0;
    const shapeName = strings.at(shapeNameIndex);
    // If the shapeNameIndex is incorrect, behave as normal
    if (!shapeName) {
        throw new Error(`Could not find a ShapeName at index '${shapeNameIndex}'`);
    }
    const prefix = strings.slice(0, Math.max(shapeNameIndex, 0));
    const suffix = strings.slice(shapeNameIndex + 1);
    let arr = [];
    if (prefix) {
        arr = [...arr, pascalCase(prefix)];
    }
    if (shapeName.length > 2 && shapeName.startsWith('TS') && shapeName.charAt(2) === shapeName.charAt(2).toUpperCase()) {
        // Built-in shape, prefixed with `TS`
        arr = [...arr, shapeName];
    }
    else {
        arr = [...arr, pascalCase(shapeName)];
    }
    if (suffix) {
        arr = [...arr, pascalCase(suffix)];
    }
    return arr.join('');
}
/**
 * Optional config triggers a namespace-preserving behavior for built-in shapes starting with `TS`.
 */
export function pascalCase(str) {
    return upperFirst(camelCase(str));
}
/** Used to match apostrophes. */
const reApos = /['\u2019]/g;
/** Has caps */
const reCap = /[A-Z]/;
export function pascalCaseWithAbbreviations(str) {
    const joined = Array.isArray(str) ? str.join(' ') : str;
    return words(deburr(joined).replace(reApos, '')).reduce((result, word) => {
        // If the word is all uppercase and contains letters,
        // treat it as an abbreviation and keep it uppercase
        if (word === word.toUpperCase() && reCap.test(word)) {
            return result + word;
        }
        // Otherwise, capitalize the word
        return result + (word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
    }, '');
}
export function isUuid(str) {
    return Boolean(/\w{8,}(-\w{4,}){3,}-\w{12,}/.exec(str));
}
export function isEmptyString(str) {
    return isString(str) && isEmpty(str);
}
export function isIntegerLike(value) {
    if (typeof value === 'number') {
        return true;
    }
    return /^\d+$/.test(value);
}
export function encodePropertyName(value) {
    return value.replace(/[^A-Za-z0-9]/g, (c) => `%${c.charCodeAt(0).toString(16)}`);
}
export function base64Encode(str) {
    return isBrowser ? window.btoa(str) : Buffer.from(str).toString('base64');
}
export function base64Decode(str) {
    return isBrowser ? window.atob(str) : Buffer.from(str, 'base64').toString();
}
/**
 * Replace {{key}} or {key} in a string with the value of the key in the data object.
 */
export function tmpl(str, data, options) {
    const pattern = options?.style === 'doubleBrace' ? /\{\{([^{}]+)\}\}/g : /\{([^{}]+)\}/g;
    return str.replace(pattern, (_, key) => {
        const value = data[key.trim()];
        return !isNil(value) ? String(value) : '';
    });
}
/**
 * Type-aware lower casing.
 */
export function lowerCase(str) {
    return str.toLowerCase();
}
